import { ApiResponse } from './types';
import { apiClient } from './core/apiClient';
import {
  ICreatePatientRequest,
  IPatientMergeHistoryItem,
  IPatientMergeRequest,
  IPatientMergeRollbackRequest,
  IPatientMergeRollbackResult,
  IPatientMergeResult,
  IPatient,
  IPatientMutationRequest,
  PatientDocumentType,
  PatientGender,
} from '../types/interfaces/IPatient';

interface PatientApiDto {
  id: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  documentType: PatientDocumentType;
  documentNumber?: string | null;
  isProvisionalIdentity: boolean;
  provisionalReason?: string | null;
  birthDate: string;
  gender: PatientGender;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  linkedUserId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  isActive?: boolean;
}

function mapPatientDto(patient: PatientApiDto): IPatient {
  const fullName =
    patient.fullName?.trim() || `${patient.firstName} ${patient.lastName}`.trim();

  return {
    id: patient.id,
    name: fullName,
    fullName,
    firstName: patient.firstName,
    lastName: patient.lastName,
    documentType: patient.documentType,
    documentNumber: patient.documentNumber || undefined,
    isProvisionalIdentity: patient.isProvisionalIdentity,
    provisionalReason: patient.provisionalReason || undefined,
    dateOfBirth: patient.birthDate,
    gender: patient.gender,
    phone: patient.phone || '',
    email: patient.email || '',
    address: patient.address || '',
    emergencyContactName: patient.emergencyContactName || '',
    emergencyContactPhone: patient.emergencyContactPhone || '',
    linkedUserId: patient.linkedUserId || undefined,
    createdAt: patient.createdAt || '',
    updatedAt: patient.updatedAt || patient.createdAt || '',
    isActive: patient.isActive ?? true,
  };
}

function mapPatientPayload(data: IPatientMutationRequest) {
  return {
    firstName: data.firstName,
    lastName: data.lastName,
    documentType: data.documentType,
    documentNumber: data.documentNumber,
    isProvisionalIdentity: data.isProvisionalIdentity,
    provisionalReason: data.provisionalReason,
    birthDate: data.dateOfBirth,
    gender: data.gender,
    phone: data.phone,
    email: data.email,
    address: data.address,
    emergencyContactName: data.emergencyContactName,
    emergencyContactPhone: data.emergencyContactPhone,
    linkedUserId: data.linkedUserId,
  };
}

export async function getPatients(
  institutionId: string
): Promise<ApiResponse<IPatient[]>> {
  const response = await apiClient.get<PatientApiDto[]>('/patients', {
    institutionId,
  });

  if (!response.success || !response.data) {
    return {
      ...response,
      data: [],
    };
  }

  return {
    ...response,
    data: response.data.map(mapPatientDto),
  };
}

export async function searchPatients(
  query: string,
  institutionId: string
): Promise<ApiResponse<IPatient[]>> {
  const response = await apiClient.get<PatientApiDto[]>('/patients/search', {
    q: query,
    institutionId,
  });

  if (!response.success || !response.data) {
    return {
      ...response,
      data: [],
    };
  }

  return {
    ...response,
    data: response.data.map(mapPatientDto),
  };
}

export async function getPatientById(
  id: string,
  institutionId: string
): Promise<ApiResponse<IPatient>> {
  const response = await apiClient.get<PatientApiDto>(`/patients/${id}`, {
    institutionId,
  });

  if (!response.success || !response.data) {
    return {
      ...response,
      data: null as unknown as IPatient,
    };
  }

  return {
    ...response,
    data: mapPatientDto(response.data),
  };
}

export async function createPatient(
  data: ICreatePatientRequest
): Promise<ApiResponse<IPatient>> {
  const response = await apiClient.post<PatientApiDto>('/patients', {
    ...mapPatientPayload(data),
    institutionId: data.institutionId,
  });

  if (!response.success || !response.data) {
    return {
      ...response,
      data: null as unknown as IPatient,
    };
  }

  return {
    ...response,
    data: mapPatientDto(response.data),
  };
}

export async function updatePatient(
  id: string,
  data: IPatientMutationRequest
): Promise<ApiResponse<IPatient>> {
  const response = await apiClient.put<PatientApiDto>(
    `/patients/${id}`,
    mapPatientPayload(data)
  );

  if (!response.success || !response.data) {
    return {
      ...response,
      data: null as unknown as IPatient,
    };
  }

  return {
    ...response,
    data: mapPatientDto(response.data),
  };
}

export async function mergePatients(
  payload: IPatientMergeRequest
): Promise<ApiResponse<IPatientMergeResult>> {
  return apiClient.post<IPatientMergeResult>('/patients/merge', payload);
}

export async function getPatientMergeHistory(
  institutionId: string,
  take: number = 20
): Promise<ApiResponse<IPatientMergeHistoryItem[]>> {
  return apiClient.get<IPatientMergeHistoryItem[]>('/patients/merge/history', {
    institutionId,
    take,
  });
}

export async function rollbackPatientMerge(
  mergeLogId: string,
  payload: IPatientMergeRollbackRequest
): Promise<ApiResponse<IPatientMergeRollbackResult>> {
  return apiClient.post<IPatientMergeRollbackResult>(
    `/patients/merge/${mergeLogId}/rollback`,
    payload
  );
}
